<template>
    <div class="spark-sequence-container">
        <div class="spark-sequence-header">
            <h5 class="spark-sequence-header__title">
                <i class="fa-solid fa-arrow-down-1-9 text-vertex-blue"></i>
                <span>Section Sequence</span>
            </h5>
            <button class="spark-btn-header spark-btn-header--primary" @click="saveSequence" :disabled="isSaving || !initialSections.length">
                <i class="fa-solid fa-floppy-disk me-1"></i> {{ isSaving ? 'Saving...' : 'Save Sequence' }}
            </button>
        </div>
        <p class="spark-sequence-hint">
            Drag sections to reorder. Toggle the eye icon to show or hide a section from the client view.
        </p>

        <draggable v-model="sections" group="sections" item-key="section_name" 
            handle=".spark-sequence-handle"
            @end="updateSequenceNumbers" animation="200"
            class="spark-sequence-list">
            <template #item="{ element, index }">
                <div class="spark-sequence-item"
                    :class="{ 'spark-sequence-item--hidden': element.is_hidden }">

                    <span class="spark-sequence-handle" title="Drag to reorder">
                        <i class="fa-solid fa-grip-vertical"></i>
                    </span>

                    <span class="spark-sequence-num">{{ index + 1 }}</span>

                    <span class="spark-sequence-name">
                        {{ formatSectionName(element.section_name) }}
                    </span>

                    <span v-if="element.is_hidden" class="spark-sequence-badge">Hidden</span>

                    <button type="button" class="spark-sequence-visibility-btn"
                        :class="{ 'spark-sequence-visibility-btn--hidden': element.is_hidden }"
                        :title="element.is_hidden ? 'Section is hidden — click to show' : 'Section is visible — click to hide'"
                        @click.stop="toggleVisibility(index)">
                        <i class="fa-solid" :class="element.is_hidden ? 'fa-eye-slash' : 'fa-eye'"></i>
                    </button>
                </div>
            </template>
        </draggable>
    </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
    components: {
        draggable
    },
    props: {
        proposalId: {
            type: String,
            required: true
        },
        initialSections: { type: Array, default: () => [] }
    },
    data() {
        return {
            isSaving: false,
            sections: []
        }
    },
    watch: {
        initialSections: {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    this.sections = JSON.parse(JSON.stringify(newVal)); 
                }
            },
            immediate: true
        }
    },
    methods: {
        formatSectionName(name) {
            const names = {
                "CoverPage": "Cover Page",
                "ProductService": "Product & Services",
                "PricingTable": "Pricing Table",
                "ImageGrid": "Image Grid",
                "Testimonials": "Testimonials",
                "SocialImpact": "Social Impact",
                "Commitment": "Commitment to Safety and Compliance",
                "WHS": "WHS Responsibilities",
                "TermsConditions": "Terms & Conditions",
                "NextSteps": "Next Steps"
            };
            return names[name] || name;
        },

        toggleVisibility(index) {
            this.sections[index].is_hidden = !this.sections[index].is_hidden;
        },

        updateSequenceNumbers() {
            this.sections.forEach((section, index) => {
                section.sequence = index + 1;
            });
        },

        async saveSequence() {
            this.isSaving = true;
            try {
                this.updateSequenceNumbers();

                const response = await this.$axios.put(`/proposals/${this.proposalId}/sequence`, this.sections);

                if (response.data.success) {
                    this.$toast.success("Section sequence updated successfully!");
                    this.$emit('sequence-updated', this.sections);
                } else {
                    this.$toast.error(response.data.message || "Failed to save sequence.");
                }
            } catch (error) {
                this.$toast.error("An error occurred while saving the sequence.");
                console.error(error);
            } finally {
                this.isSaving = false;
            }
        }
    }
}
</script>

<style scoped>
/* Target the whole row for the grab cursors instead of just the handle */
.draggable-row {
    cursor: grab;
}

.draggable-row:active {
    cursor: grabbing !important;
}

.transition-all {
    transition: all 0.2s ease;
}

.text-vertex-blue {
    color: #00A0C1;
}
</style>